import { translateTemplateText } from '@focuslee/shared-mini-app';
import { Box, Stack } from '@mui/material';
import { Text } from '@telegram-apps/telegram-ui';
import { type FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { QuestMetricType } from '../../client/generated/graphql';
import { ListCategory, ListQuest } from '../../client/types';
import {
  formatAchievementConditionQuestSummary,
  getConditionIcon,
} from '../../helpers/achievement';
import { findCategoryByName } from '../../helpers/category';
import { getColor } from '../../helpers/color';
import { voiceActionDisplayTitle } from '../../helpers/voiceActionDisplay';
import { findQuestByName } from '../../helpers/quest';
import { useCachedCategories } from '../../hooks/caching/category';
import { useCachedQuests } from '../../hooks/caching/quest';
import type { CreateAchievementVoiceAction, CreateQuestVoiceAction, VoiceAction } from '../../types/voice';
import { CategoryIcon } from '../common/CategoryIcon';
import { CommonIcon } from '../common/CommonIcon';

const hintStyle = { fontSize: 11, color: 'var(--tg-theme-hint-color)', lineHeight: 1.35 };

interface ActionCardCreateAchievementViewProps {
  action: CreateAchievementVoiceAction;
  dependentActions?: VoiceAction[];
}

function resolveCategoryForAchievement(
  action: CreateAchievementVoiceAction,
  parentQuests: CreateQuestVoiceAction[],
  categories: ListCategory[],
) {
  const fromActionId = action.categoryId ? categories.find((c) => c.id === action.categoryId) : undefined;
  const fromActionName = action.categoryName ? findCategoryByName(categories, action.categoryName) : undefined;
  if (fromActionId ?? fromActionName) return fromActionId ?? fromActionName ?? null;
  for (const parentQuest of parentQuests) {
    const fromParentId = parentQuest.categoryId ? categories.find((c) => c.id === parentQuest.categoryId) : undefined;
    const fromParentName = parentQuest.categoryName
      ? findCategoryByName(categories, parentQuest.categoryName)
      : undefined;
    const hit = fromParentId ?? fromParentName;
    if (hit) return hit;
  }
  return null;
}

function resolveQuestNames(
  action: CreateAchievementVoiceAction,
  dependentActions: VoiceAction[],
  quests: ListQuest[],
): string[] {
  const extraIds = action.conditionQuestIds ?? [];
  const extraNames = extraIds.map((id) => quests.find((q) => q.id === id)?.name ?? id);
  const linkedNames = dependentActions
    .filter((d): d is CreateQuestVoiceAction => d.type === 'CREATE_QUEST')
    .map((d) => voiceActionDisplayTitle(d) || d.questName);
  if (linkedNames.length) {
    if (extraNames.length) return [...linkedNames, ...extraNames];
    return linkedNames;
  }
  if (extraNames.length) return extraNames;
  if (action.questIds?.length) {
    return action.questIds.map((id) => quests.find((q) => q.id === id)?.name ?? id);
  }
  if (action.questNames?.length) {
    return action.questNames.map((name) => findQuestByName(quests, name)?.name ?? name);
  }
  return [];
}

export const ActionCardCreateAchievementView: FC<ActionCardCreateAchievementViewProps> = ({
  action,
  dependentActions = [],
}) => {
  const { t } = useTranslation();
  const categories = useCachedCategories();
  const quests = useCachedQuests();

  const parentQuests = useMemo(
    () => dependentActions.filter((d): d is CreateQuestVoiceAction => d.type === 'CREATE_QUEST'),
    [dependentActions],
  );

  const category = useMemo(
    () => resolveCategoryForAchievement(action, parentQuests, categories),
    [action, parentQuests, categories],
  );

  const questNames = useMemo(
    () => resolveQuestNames(action, dependentActions, quests),
    [action, dependentActions, quests],
  );

  const metricType = action.conditionMetricType ?? QuestMetricType.Completion;
  const target = action.conditionTarget ?? null;

  const conditionSummary = useMemo(
    () => formatAchievementConditionQuestSummary(metricType, target, questNames),
    [metricType, target, questNames],
  );

  const conditionIconName = getConditionIcon(metricType);

  return (
    <Stack gap={0.75} sx={{ minWidth: 0, alignItems: 'stretch' }}>
      {category && (
        <Stack direction="row" alignItems="center" gap={0.5} flexShrink={0}>
          <Box
            sx={{
              width: 16,
              height: 16,
              borderRadius: 0.75,
              background: getColor(category.iconColor),
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <CategoryIcon
              icon={category.icon}
              iconColor={category.iconColor}
              customIcon={category.customIcon}
              type="filled"
              size={10}
              color="var(--tg-theme-text-color)"
            />
          </Box>
          <Text style={{ ...hintStyle, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {translateTemplateText(t, category.templateId, 'name', category.name)}
          </Text>
        </Stack>
      )}
      {questNames.length > 0 && (
        <Stack direction="row" alignItems="flex-start" gap={0.75} sx={{ minWidth: 0 }}>
          <Box sx={{ flexShrink: 0, pt: 0.125 }}>
            <CommonIcon
              icon={conditionIconName}
              type="filled"
              size={16}
              color="var(--tg-theme-hint-color)"
            />
          </Box>
          <Text
            style={{
              ...hintStyle,
              flex: 1,
              minWidth: 0,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {conditionSummary}
          </Text>
        </Stack>
      )}
    </Stack>
  );
};
